import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useT } from "../lib/LanguageContext";

const LINKS = [
  { to: "/", slug: "accueil" },
  { to: "/a-propos", slug: "apropos" },
  { to: "/vision", slug: "vision" },
  { to: "/offres", slug: "offres" },
  { to: "/contact", slug: "contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useT();
  const isHome = location.pathname === "/";
  const onDark = isHome && !open;
  const logoWhite = isHome && !open && !scrolled;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      style={{ transition: "background-color 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease" }}
      className={
        open
          ? "fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-b border-[#f0e8c8]/60"
          : "fixed top-0 left-0 right-0 z-40 bg-transparent border-b border-transparent"
      }
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-4">
        <Logo white={logoWhite} />

        <div className="flex items-center gap-6">
          <nav className="hidden md:flex items-center gap-8 lg:gap-10">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.slug}`}
                className={({ isActive }) =>
                  `text-[13px] uppercase tracking-[0.22em] font-medium transition-colors duration-300 ${
                    isActive
                      ? "text-[#6B5CE7]"
                      : onDark
                        ? "text-white hover:text-[#F5E6A3]"
                        : "text-[#1A1528] hover:text-[#6B5CE7]"
                  }`
                }
                end={l.to === "/"}
              >
                {t(`nav.${l.slug}`)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className={`md:hidden p-2 rounded-full ${onDark ? "text-white" : "text-[#1A1528]"}`}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-[#f0e8c8]">
          <div className="flex flex-col px-6 py-6 gap-4">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mnav-${l.slug}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.22em] py-2 ${
                    isActive ? "text-[#6B5CE7]" : "text-[#1A1528]"
                  }`
                }
                end={l.to === "/"}
              >
                {t(`nav.${l.slug}`)}
              </NavLink>
            ))}
            <div className="pt-4 border-t border-[#f0e8c8]">
              <LanguageSwitcher compact />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
